import React, { useState } from 'react';
import { Play, X } from 'lucide-react';
import { useSite } from '../context/SiteContext';

const YouTubeModal = ({ videoId, onClose, title }) => (
  <div
    className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
    onClick={onClose}
  >
    <div
      className="relative w-full max-w-5xl aspect-video rounded-xl overflow-hidden border border-white/10 bg-black"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute -top-3 -right-3 z-10 h-9 w-9 rounded-full bg-white text-black flex items-center justify-center hover:bg-brand-green-light btn-transition"
      >
        <X className="h-4 w-4" />
      </button>
      <iframe
        title={title}
        src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="w-full h-full"
      />
    </div>
  </div>
);

const VideoTile = ({ videoId, poster, title, badge, size = 'md', onOpen }) => (
  <button
    onClick={onOpen}
    className={`group relative rounded-2xl overflow-hidden border border-white/8 bg-white/[0.02] card-lift w-full text-left ${
      size === 'lg' ? 'aspect-video' : 'aspect-[16/10]'
    }`}
    aria-label={`Play ${title}`}
  >
    <img src={poster} alt={title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
    <div
      aria-hidden
      className="absolute inset-0"
      style={{
        background:
          'linear-gradient(180deg, rgba(6,8,13,0.15) 0%, rgba(6,8,13,0.75) 100%)',
      }}
    />
    <span className="absolute top-4 left-4 rounded-md bg-black/50 border border-white/10 px-2.5 py-1 text-[11px] uppercase tracking-widest text-white">
      {badge}
    </span>
    <span
      className={`absolute inset-0 flex items-center justify-center`}
    >
      <span
        className={`${size === 'lg' ? 'h-20 w-20' : 'h-14 w-14'} rounded-full bg-brand-green text-black flex items-center justify-center shadow-lg shadow-brand-green/40 group-hover:scale-110 btn-transition`}
      >
        <Play className={`${size === 'lg' ? 'h-8 w-8' : 'h-5 w-5'} fill-current`} />
      </span>
    </span>
    <span className="absolute bottom-4 left-4 right-4 text-white font-medium text-sm md:text-base line-clamp-2">
      {title}
    </span>
  </button>
);

const Showreel = () => {
  const { showreel } = useSite();
  const [active, setActive] = useState(null);
  if (!showreel) return null;

  return (
    <section id="showreel" className="section pt-16 md:pt-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-brand-pink-light">{showreel.eyebrow}</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold text-white leading-tight">
              {showreel.heading}
            </h2>
          </div>
          <p className="max-w-md text-slate-400">{showreel.body}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2">
            <VideoTile
              size="lg"
              videoId={showreel.hero.videoId}
              poster={showreel.hero.poster}
              title={showreel.hero.title}
              badge={showreel.hero.badge}
              onOpen={() => setActive(showreel.hero)}
            />
          </div>
          <div className="grid gap-5">
            {showreel.thumbs.map((t) => (
              <VideoTile
                key={t.videoId}
                videoId={t.videoId}
                poster={t.poster}
                title={t.title}
                badge={t.badge}
                onOpen={() => setActive(t)}
              />
            ))}
          </div>
        </div>
      </div>

      {active && (
        <YouTubeModal videoId={active.videoId} title={active.title} onClose={() => setActive(null)} />
      )}
    </section>
  );
};

export default Showreel;
